import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
} from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { verifyEmail as verifyEmailApi } from "../services/User";
import { useDispatch } from "react-redux";
import { fetchLoggedInUser } from "../redux/slices/userSlice";

/**
 * Email verification landing page.
 * User arrives here via the link in their verification email.
 */
export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    verifyEmailApi(token)
      .then((res) => {
        setStatus("success");
        setMessage(res.message || "Your email has been verified!");
        // Refresh user state so the verification banner disappears
        if (localStorage.getItem("token")) {
          dispatch(fetchLoggedInUser());
        }
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err.response?.data?.message ||
            "Verification failed. The link may be expired.",
        );
      });
  }, [token, dispatch]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 5,
          textAlign: "center",
          maxWidth: 440,
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        {status === "loading" && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your email…</Typography>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutline
              sx={{ fontSize: 64, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Email Verified!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Button variant="contained" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <ErrorOutline sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Verification Failed
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Button variant="outlined" onClick={() => navigate("/auth")}>
              Back to Login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
